import { Badge, Breadcrumb, HeaderToolbar, Tabs } from "@xola/ui-kit";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Resource, ResourceCreatedBy } from "types";
import { NoTranslate } from "../../components/Common/NoTranslate";
import { PluginImage } from "../../components/Common/PluginImage";
import { ColumnLayout } from "../../components/Layout/ColumnLayout";
import { CreateEquipment } from "../../components/Resources/EquipmentManagement/CreateEquipment";
import { EditEquipment } from "../../components/Resources/EquipmentManagement/EditEquipment";
import { EditSchedule } from "../../components/Resources/EquipmentManagement/EditSchedule";
import { EditEquipmentTab } from "../../components/Resources/EquipmentManagement/types";
import { useSeller } from "../../hooks/useAccount";
import { useInstalledPlugins } from "../../hooks/usePlugins";
import { useResource } from "../../hooks/useResource";
import { Page } from "../Page";
import { EmbeddedAppTriggerLocation } from "@/embeddedApps/EmbeddedAppTriggerLocation";

export const EditEquipmentPage = () => {
    const seller = useSeller();
    const { id, step: urlStep } = useParams<{ id?: string; step?: string }>();
    const { data: resource } = useResource<Resource>(["resources", id]);
    const history = useHistory();
    const isSchedulesAllowed = seller.preferences.inventory.allowSchedules;

    const step = useMemo<EditEquipmentTab | undefined>(() => (urlStep ? Number(urlStep) : undefined), [urlStep]);

    const handleResourceListClick = () => {
        history.push("/resources/equipment");
    };

    const [activeTab, setActiveTab] = useState<EditEquipmentTab>(EditEquipmentTab.Equipment);

    const changeTab = (tabIndex: EditEquipmentTab) => {
        history.push(`/resources/equipment/${id}/${tabIndex}`);
    };

    const hasPermission = useCallback(
        (tab: EditEquipmentTab) => {
            switch (tab) {
                case EditEquipmentTab.Equipment:
                    return true;
                case EditEquipmentTab.Schedule:
                    return isSchedulesAllowed;
                default:
                    return false;
            }
        },
        [isSchedulesAllowed],
    );

    useEffect(() => {
        if (step !== undefined) {
            setActiveTab(hasPermission(step) ? step : EditEquipmentTab.Equipment);
        }
    }, [step, hasPermission]);

    return (
        <Page title="Equipment Management">
            <ColumnLayout>
                <HeaderToolbar>
                    <HeaderToolbar.Breadcrumb className="flex-grow truncate border-r-0">
                        <Breadcrumb.Item>Resources</Breadcrumb.Item>
                        <Breadcrumb.Item onClick={handleResourceListClick}>Equipment Management</Breadcrumb.Item>
                        <Breadcrumb.Item>
                            {resource?.name ? <NoTranslate>{resource.name}</NoTranslate> : "New Equipment Item"}
                        </Breadcrumb.Item>
                    </HeaderToolbar.Breadcrumb>
                    {resource?.createdBy?.type === ResourceCreatedBy.ZOOM && <ZoomBadge />}
                    {id && (
                        <EmbeddedAppTriggerLocation
                            id="resources.equipment.detail.headerBar"
                            context={{ equipment: [{ id }] }}
                        />
                    )}
                </HeaderToolbar>
                <ColumnLayout.Fill>
                    {id ? (
                        <Tabs className="w-full" value={activeTab} onChange={changeTab}>
                            <Tabs.Tab>Basic Info</Tabs.Tab>
                            <Tabs.Panel className="p-4">
                                <EditEquipment resource={resource} />
                            </Tabs.Panel>

                            <Tabs.Tab isHidden={!isSchedulesAllowed}>Schedule</Tabs.Tab>
                            <Tabs.Panel className="p-4">
                                {isSchedulesAllowed && <EditSchedule resource={resource} />}
                            </Tabs.Panel>
                        </Tabs>
                    ) : (
                        <CreateEquipment />
                    )}
                </ColumnLayout.Fill>
            </ColumnLayout>
        </Page>
    );
};

export const ZoomBadge = () => {
    const { data: installedPlugins } = useInstalledPlugins();

    const zoomPlugin = installedPlugins.find((plugin) => {
        return plugin.abilities && "virtualMeeting" in plugin.abilities;
    });

    return zoomPlugin ? (
        <Badge color="primary" className="min-w-36" size="small">
            <PluginImage plugin={zoomPlugin} className="mr-1 h-4 w-4" />
            Managed by Zoom
        </Badge>
    ) : null;
};
